import React from 'react';
import SEO from '../components/SEO';

const FullStackPython: React.FC = () => {
  return (
    <>
      <SEO 
        title="Full Stack Python Training | Python Web Development Course - SBRIT Solution"
        description="Build complete web applications with Python backend and modern frontend. Learn Django, Flask, database integration, and full-stack development."
        keywords="full stack python training, python web development, django course, flask course, python backend, full stack development, python programming"
        url="https://sbritsolution.in/courses/full-stack-python"
        type="article"
      />
      
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-blue-600 to-green-600 text-white py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                Full Stack Python
              </h1>
              <p className="text-xl md:text-2xl mb-8 leading-relaxed">
                Build Complete Web Applications | Django | Flask | Frontend Integration | Database Design
              </p>
              <div className="flex flex-col md:flex-row gap-4 justify-center">
                <button className="bg-yellow-500 hover:bg-yellow-600 text-black px-8 py-4 rounded-lg font-semibold text-lg transition-colors">
                  Start Learning - ₹22,999
                </button>
                <button className="border-2 border-white hover:bg-white hover:text-blue-600 px-8 py-4 rounded-lg font-semibold text-lg transition-colors">
                  Download Syllabus
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Why Full Stack Python */}
        <div className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">Why Learn Full Stack Python?</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center p-6 rounded-lg shadow-lg">
                <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">🐍</span>
                </div>
                <h3 className="text-xl font-semibold mb-3">Versatile Language</h3>
                <p className="text-gray-600">Python is used for web development, data science, AI, and automation - making it highly versatile.</p>
              </div>
              <div className="text-center p-6 rounded-lg shadow-lg">
                <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">💼</span>
                </div>
                <h3 className="text-xl font-semibold mb-3">High Demand</h3>
                <p className="text-gray-600">Python developers are in high demand with excellent career opportunities and salary packages.</p>
              </div>
              <div className="text-center p-6 rounded-lg shadow-lg">
                <div className="bg-yellow-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">🚀</span>
                </div>
                <h3 className="text-xl font-semibold mb-3">Rapid Development</h3>
                <p className="text-gray-600">Python frameworks enable rapid development of web applications with clean, maintainable code.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Full Stack Python Curriculum */}
        <div className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">Complete Full Stack Python Curriculum</h2>
            <div className="max-w-6xl mx-auto">
              <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-white p-6 rounded-lg shadow-lg">
                  <h3 className="text-xl font-semibold mb-4 text-blue-600">Python Advanced</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Advanced Python Concepts</li>
                    <li>• Object-Oriented Programming</li>
                    <li>• Decorators & Generators</li>
                    <li>• Context Managers</li>
                    <li>• Async Programming</li>
                    <li>• Testing with pytest</li>
                    <li>• Code Quality & PEP8</li>
                    <li>• Performance Optimization</li>
                  </ul>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-lg">
                  <h3 className="text-xl font-semibold mb-4 text-green-600">Django Framework</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Django Fundamentals</li>
                    <li>• Models & Database Design</li>
                    <li>• Views & URL Patterns</li>
                    <li>• Templates & Forms</li>
                    <li>• Django Admin</li>
                    <li>• Authentication & Authorization</li>
                    <li>• Django REST Framework</li>
                    <li>• Deployment & Production</li>
                  </ul>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-lg">
                  <h3 className="text-xl font-semibold mb-4 text-purple-600">Flask Framework</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Flask Basics & Setup</li>
                    <li>• Routing & Views</li>
                    <li>• Templates & Jinja2</li>
                    <li>• Forms & Validation</li>
                    <li>• Database Integration</li>
                    <li>• Flask Extensions</li>
                    <li>• API Development</li>
                    <li>• Testing Flask Applications</li>
                  </ul>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-lg">
                  <h3 className="text-xl font-semibold mb-4 text-orange-600">Frontend Integration</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• HTML5 & CSS3</li>
                    <li>• JavaScript ES6+</li>
                    <li>• Bootstrap & Responsive Design</li>
                    <li>• AJAX & API Integration</li>
                    <li>• React.js Basics</li>
                    <li>• Frontend Build Tools</li>
                    <li>• Progressive Web Apps</li>
                    <li>• Mobile-First Design</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tools & Technologies */}
        <div className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">Tools & Technologies You'll Master</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="text-center p-6 rounded-lg shadow-lg border">
                <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">🐍</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">Python</h3>
                <p className="text-gray-600 text-sm">Advanced Python programming and best practices</p>
              </div>
              <div className="text-center p-6 rounded-lg shadow-lg border">
                <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">⚡</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">Django</h3>
                <p className="text-gray-600 text-sm">Full-featured web framework for rapid development</p>
              </div>
              <div className="text-center p-6 rounded-lg shadow-lg border">
                <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">🔥</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">Flask</h3>
                <p className="text-gray-600 text-sm">Lightweight and flexible micro-framework</p>
              </div>
              <div className="text-center p-6 rounded-lg shadow-lg border">
                <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">🗄️</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">PostgreSQL</h3>
                <p className="text-gray-600 text-sm">Advanced open-source database system</p>
              </div>
            </div>
          </div>
        </div>

        {/* Course Features */}
        <div className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">What You'll Get</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="text-center p-6 rounded-lg shadow-lg border">
                <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-xl">📚</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">Comprehensive Content</h3>
                <p className="text-gray-600 text-sm">80+ hours of structured learning with hands-on projects</p>
              </div>
              <div className="text-center p-6 rounded-lg shadow-lg border">
                <div className="bg-green-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-xl">💻</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">Real Projects</h3>
                <p className="text-gray-600 text-sm">Build complete web applications from scratch</p>
              </div>
              <div className="text-center p-6 rounded-lg shadow-lg border">
                <div className="bg-purple-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-xl">🎓</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">Certification</h3>
                <p className="text-gray-600 text-sm">Industry-recognized certificate upon completion</p>
              </div>
              <div className="text-center p-6 rounded-lg shadow-lg border">
                <div className="bg-orange-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-xl">👥</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">Expert Support</h3>
                <p className="text-gray-600 text-sm">24/7 support from Python development experts</p>
              </div>
            </div>
          </div>
        </div>

        {/* Career Opportunities */}
        <div className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">Career Opportunities</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white p-6 rounded-lg shadow-lg text-center border">
                <h3 className="text-xl font-semibold mb-4 text-blue-600">Python Developer</h3>
                <p className="text-gray-600 mb-4">Build web applications and backend services with Python</p>
                <div className="text-sm text-gray-500">Average Salary: ₹6-12 LPA</div>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-lg text-center border">
                <h3 className="text-xl font-semibold mb-4 text-green-600">Full Stack Developer</h3>
                <p className="text-gray-600 mb-4">Develop complete web applications from frontend to backend</p>
                <div className="text-sm text-gray-500">Average Salary: ₹8-15 LPA</div>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-lg text-center border">
                <h3 className="text-xl font-semibold mb-4 text-purple-600">Backend Engineer</h3>
                <p className="text-gray-600 mb-4">Design and implement robust backend systems and APIs</p>
                <div className="text-sm text-gray-500">Average Salary: ₹7-14 LPA</div>
              </div>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="py-16 bg-gradient-to-r from-blue-600 to-green-600 text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold mb-6">Ready to Build Complete Web Applications?</h2>
            <p className="text-xl mb-8">Join thousands of developers building amazing applications with Python</p>
            <div className="flex flex-col md:flex-row gap-4 justify-center">
              <button className="bg-yellow-500 hover:bg-yellow-600 text-black px-8 py-4 rounded-lg font-semibold text-lg transition-colors">
                Enroll Now - ₹22,999
              </button>
              <button className="border-2 border-white hover:bg-white hover:text-blue-600 px-8 py-4 rounded-lg font-semibold text-lg transition-colors">
                Download Course Brochure
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FullStackPython;
